import React, { useEffect, useState } from 'react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { StatusBadge } from '../components/StatusBadge';
import { userData, recentExpenses } from '../utils/mockData';
import { ReceiptIcon, TrendingUpIcon, InfoIcon, MapPinIcon } from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/formatters';
import { SkeletonCard } from '../components/Skeleton';
import { PerDiemModal } from '../components/PerDiemModal';
import { NotificationBell } from '../components/NotificationBell';
import { getConusPerDiem, getOconusPerDiem } from '../utils/perDiem';
export function Home({
  navigateTo
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isPerDiemModalOpen, setIsPerDiemModalOpen] = useState(false);
  const [perDiem, setPerDiem] = useState<{
    lodging: number;
    mie: number;
    loc: string;
  }>();
  const [location, setLocation] = useState(userData.location);
  // Function to get the current month key in the format used by GSA API
  function monthKey() {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short'
    }).format(new Date()).slice(0, 3);
  }
  // Simulate loading state
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);
  // Fetch per diem based on geolocation
  useEffect(() => {
    const success = async p => {
      const {
        latitude,
        longitude
      } = p.coords;
      const geo = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`).then(r => r.json());
      const {
        postcode: zip,
        country_code,
        city,
        town,
        village,
        state
      } = geo.address || {};
      if (country_code === 'us' && zip) {
        const d = await getConusPerDiem(zip);
        setPerDiem({
          lodging: +d[monthKey()],
          mie: +d.Meals,
          loc: `${city || town || village}, ${state}`
        });
        setLocation(`${city || town || village}, ${state}`);
      } else {
        const intl = await getOconusPerDiem(geo.address?.country, city || town || village);
        if (intl) {
          setPerDiem({
            lodging: intl.lodging,
            mie: intl.mie,
            loc: `${intl.city}, ${intl.country}`
          });
          setLocation(`${intl.city}, ${intl.country}`);
        }
      }
    };
    const error = () => {
      /* fall back to ZIP form */
      // ZIP form is already in the UI when perDiem is null, so no additional action needed
    };
    navigator.geolocation.getCurrentPosition(success, error);
  }, []);
  return <div className="p-4 max-w-md mx-auto" data-id="element-196">
      <div className="flex items-center justify-between mb-6" data-id="element-197">
        <div className="flex items-center" data-id="element-198">
          <img src={userData.avatar} alt="User avatar" className="w-12 h-12 rounded-full mr-3" data-id="element-199" />
          <div data-id="element-200">
            <h1 className="text-xl font-bold" data-id="element-201">Welcome back,</h1>
            <p className="text-gray-600" data-id="element-202">{userData.name}</p>
            <p className="text-xs text-gray-500" data-id="element-203">{userData.company}</p>
          </div>
        </div>
        <NotificationBell data-id="element-204" />
      </div>
      <Card className="mb-6 bg-blue-50 border-blue-100 transform transition-all hover:scale-[1.02]" data-id="element-205">
        <div className="flex justify-between items-start" data-id="element-206">
          <div data-id="element-207">
            <div className="flex items-center gap-2" data-id="element-208">
              <p className="text-sm text-blue-700 font-medium" data-id="element-209">
                Today's Per Diem
              </p>
              <button onClick={() => setIsPerDiemModalOpen(true)} className="p-1 hover:bg-blue-100 rounded-full transition-colors" data-id="element-210">
                <InfoIcon size={14} className="text-blue-600" data-id="element-211" />
              </button>
            </div>
            <div className="flex items-baseline" data-id="element-212">
              <span className="text-2xl font-bold text-blue-800" data-id="element-213">
                {formatCurrency(userData.perDiemRemaining)}
              </span>
              <span className="text-sm text-blue-600 ml-1" data-id="element-214">
                / {formatCurrency(userData.perDiemTotal)}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-2 text-sm text-blue-600" data-id="element-215">
              <MapPinIcon size={14} data-id="element-216" />
              <span data-id="element-217">{location}</span>
            </div>
          </div>
          <div className="bg-blue-100 p-3 rounded-full" data-id="element-218">
            <TrendingUpIcon className="text-blue-700" size={20} data-id="element-219" />
          </div>
        </div>
        {perDiem && <div className="mt-4 p-4 rounded-xl shadow bg-white dark:bg-slate-800" data-id="element-220">
            <h3 className="font-semibold mb-2" data-id="element-221">Per Diem for {perDiem.loc}</h3>
            <p data-id="element-222">
              Lodging cap: <strong data-id="element-223">${perDiem.lodging}</strong>
            </p>
            <p data-id="element-224">
              M&I cap: <strong data-id="element-225">${perDiem.mie}</strong>
            </p>
          </div>}
        {!perDiem && <form onSubmit={async e => {
        e.preventDefault();
        const zip = (e.currentTarget.elements.namedItem('zip') as HTMLInputElement).value;
        if (!zip) return;
        const data = await getConusPerDiem(zip);
        setPerDiem({
          lodging: +data[monthKey()],
          mie: +data.Meals,
          loc: `${zip}, US`
        });
      }} className="mt-4" data-id="element-226">
            <label className="block mb-2" data-id="element-227">Enter ZIP to get rates</label>
            <div className="flex" data-id="element-228">
              <input name="zip" className="border p-2 rounded-l mr-0 flex-1" placeholder="e.g. 30303" data-id="element-229" />
              <button className="px-4 py-2 bg-blue-600 text-white rounded-r" data-id="element-230">
                Lookup
              </button>
            </div>
          </form>}
      </Card>
      <PerDiemModal isOpen={isPerDiemModalOpen} onClose={() => setIsPerDiemModalOpen(false)} currentLocation={location} currentAmount={userData.perDiemTotal} data-id="element-231" />
      <Button onClick={() => navigateTo('upload')} fullWidth className="mb-6 transform transition-all active:scale-95" icon={<ReceiptIcon size={20} data-id="element-233" />} data-id="element-232">
        Upload Receipt
      </Button>
      <div className="mb-4 flex justify-between items-center" data-id="element-234">
        <h2 className="text-lg font-bold" data-id="element-235">Recent Submissions</h2>
        <button onClick={() => navigateTo('history')} className="text-sm text-blue-600 hover:text-blue-700 transition-colors" data-id="element-236">
          View All
        </button>
      </div>
      {isLoading ? <>
          <SkeletonCard data-id="element-237" />
          <SkeletonCard data-id="element-238" />
          <SkeletonCard data-id="element-239" />
        </> : recentExpenses.map(expense => <Card key={expense.id} className="mb-3 transform transition-all hover:scale-[1.02]" onClick={() => navigateTo('history')} data-id="element-240">
            <div className="flex justify-between items-center" data-id="element-241">
              <div className="flex items-center" data-id="element-242">
                <div className="bg-gray-100 p-2 rounded-full mr-3" data-id="element-243">
                  <ReceiptIcon size={18} className="text-gray-500" data-id="element-244" />
                </div>
                <div data-id="element-245">
                  <p className="font-medium" data-id="element-246">{expense.merchant}</p>
                  <p className="text-sm text-gray-500" data-id="element-247">
                    {formatDate(expense.date)}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end" data-id="element-248">
                <p className="font-bold" data-id="element-249">{formatCurrency(expense.amount)}</p>
                <StatusBadge status={expense.status} data-id="element-250" />
              </div>
            </div>
          </Card>)}
    </div>;
}